import { Activity, CircleAlert, CreditCard, Mail, MoreHorizontal, Pause, PauseCircle, Trash2, Undo2 } from 'lucide-react'
import { useId, type ReactNode } from 'react'

import { Button } from '@krp/ui-kit'

import { RmkNotice } from './RmkNotice'
import type { PaymentKind, SaleVm } from '../port/types'

/** Смена кассы для строки колонки расчёта: знак и слово, а не только цвет (CLAUDE.md §7). */
export type ShiftMark = 'open' | 'closed' | 'unknown'

const SHIFT_TEXT: Readonly<Record<ShiftMark, string>> = {
  open: '● Смена открыта',
  closed: '○ Смена закрыта',
  unknown: '◌ Смена не подтверждена',
}

/** Что колонке расчёта нужно от кассы: смена, где работаем, «Действия». */
export interface CheckoutTill {
  shift: ShiftMark
  /** «Касса 1 · Вольная 28». */
  where: string
  /** Оплату нельзя начать по причине кассы (смена закрыта) — словами. */
  payBlockedReason: string | null
  onActions: () => void
  /** Фоновая работа источника: видна рядом с новой продажей. */
  backgroundWork?: ReactNode
  /** Текущее чтение состояния кассы; не закрывает корзину. */
  shiftStatus?: ReactNode
}

/**
 * Колонка расчёта старого РМК (решение владельца 23.10.2026: без прокрутки, только постоянные элементы): «● Смена открыта» и
 * «··· Действия»; «Итого» крупно; «Цены кассы на 14:32»; «Покупателю», «Отложить», «Очистить»; «Отложенные · N»; «Наличными» и «Картой»
 * всегда прижаты к низу и не сдвигаются. Статусы, ошибки и ход работы — в блоке состояния под чеком (`CheckoutStatus`), а не здесь.
 * `dock` — телефон: итог и оплата уходят в нижнюю полосу (`CheckoutDock`), здесь остальное.
 */
export function Checkout({
  vm,
  till,
  contactOpen,
  dock,
  onToggleContact,
  onContact,
  onHold,
  onClear,
  onHeld,
  onPay,
}: {
  vm: SaleVm
  till: CheckoutTill
  contactOpen: boolean
  dock: boolean
  onToggleContact: () => void
  onContact: (value: string) => void
  onHold: () => void
  onClear: () => void
  onHeld: () => void
  onPay: (kind: PaymentKind) => void
}) {
  const contactId = useId()
  const contact = vm.customerContact.trim()
  return (
    <aside className="rmk-checkout" aria-label="Расчёт">
      <div className="rmk-checkout__top">
        <span className="rmk-shift" data-shift={till.shift} role="status">
          {SHIFT_TEXT[till.shift]}
        </span>
        <Button variant="secondary" icon={<MoreHorizontal size={16} aria-hidden="true" />} onClick={till.onActions}>
          Действия
        </Button>
      </div>
      <p className="rmk-checkout__where" title={till.where}>
        {till.where}
      </p>
      <div className="rmk-checkout__body">
        {!dock && <Total vm={vm} />}
        {vm.pricesAtText !== null && <p className="rmk-checkout__prices">{vm.pricesAtText}</p>}

        <div className="rmk-checkout__actions" role="group" aria-label="Действия с чеком">
          <Button
            icon={<Mail size={15} aria-hidden="true" />}
            aria-label="Чек покупателю"
            aria-expanded={contactOpen}
            disabled={!vm.canEditContact}
            onClick={onToggleContact}
          >
            Покупателю
          </Button>
          <Button icon={<Pause size={15} aria-hidden="true" />} aria-label="Отложить чек" disabled={!vm.canHold} onClick={onHold}>
            Отложить
          </Button>
          <Button icon={<Trash2 size={15} aria-hidden="true" />} aria-label="Очистить чек" disabled={!vm.canClear} onClick={onClear}>
            Очистить
          </Button>
        </div>

        {contactOpen ? (
          <div className="krp-field rmk-checkout__contact-field">
            <label className="krp-field__label" htmlFor={contactId}>
              Телефон или почта покупателя
            </label>
            <input
              id={contactId}
              // Поле раскрыто кнопкой — фокус сразу в нём.
              autoFocus
              inputMode="email"
              autoComplete="off"
              value={vm.customerContact}
              disabled={!vm.canEditContact}
              onChange={(event) => onContact(event.target.value)}
            />
          </div>
        ) : (
          contact !== '' && (
            <p className="rmk-checkout__contact">
              <Mail size={14} aria-hidden="true" /> Чек уйдёт: <b>{contact}</b>
            </p>
          )
        )}

        {vm.held.carts.length > 0 && (
          <div className="rmk-checkout__held">
            <Button icon={<Undo2 size={15} aria-hidden="true" />} onClick={onHeld}>
              Отложенные · {vm.held.carts.length}
            </Button>
          </div>
        )}
      </div>

      {!dock && <Payments vm={vm} till={till} onPay={onPay} />}
    </aside>
  )
}

/** Почему оплату сейчас не начать — словами (смена, сверка, касса); `null` — можно. */
function payBlockedText(vm: SaleVm, till: CheckoutTill): string | null {
  return till.payBlockedReason ?? (vm.lines.length > 0 && !vm.payButtons.some((button) => button.enabled) ? vm.payBlockedReason : null)
}

/**
 * Блок состояния кассы под чеком, справа (решение владельца 23.10.2026): всё, что меняется и требует внимания, — чтение смены, связь с
 * сервером и кассой, почему оплата недоступна, прошлый отказ оплаты, найденный после отмены слип (кнопкой в окно оплаты), заметки
 * источника, свёрнутый ход оплаты и фоновые чеки. Колонка расчёта от этого не растёт и не прокручивается: блок живёт под чеком и сам ограничен по высоте.
 */
export function CheckoutStatus({
  vm,
  till,
  onShowPayment,
  onOpenParked,
  onNoticeAction,
  onNoticeDismiss,
}: {
  vm: SaleVm
  till: CheckoutTill
  /** Развернуть окно оплаты: «Ход оплаты: …» свёрнутого окна и строка позднего слипа. */
  onShowPayment: () => void
  /** Перейти к списку отложенных продаж; нет — в заметке только текст. */
  onOpenParked?: (() => void) | undefined
  /** Действие заметки источника и её закрытие; нет — у заметки только текст. */
  onNoticeAction?: ((key: string) => void) | undefined
  onNoticeDismiss?: ((key: string) => void) | undefined
}) {
  const reason = payBlockedText(vm, till)
  return (
    <section className="rmk-status" aria-label="Состояние кассы">
      {till.shiftStatus}
      {/* Связь здесь не пишется (RP-UI-LINK): источник сообщает её сигналом в точку «Связь» шапки окна. */}
      {reason !== null && (
        <p className="rmk-status__line" role="status">
          <CircleAlert size={14} aria-hidden="true" /> {reason}
        </p>
      )}
      {/* Почему корзину сейчас менять нельзя — словами (RP-SALE-FAST, К-106); пока идёт окно оплаты, об этом говорит оно. */}
      {vm.cartLocked && vm.cartLockedReason != null && vm.cartLockedReason !== reason && vm.payment == null && (
        <p className="rmk-status__line" role="status">
          <CircleAlert size={14} aria-hidden="true" /> {vm.cartLockedReason}
        </p>
      )}
      {/* Об оплате без окна — строкой, не уведомлением внизу (RP-PAY-ONE): «Прошлая оплата не прошла: …», предупреждения службы. */}
      {vm.paymentStatus !== null && (
        <p className="rmk-status__line" role="status" data-tone="warning">
          <CircleAlert size={14} aria-hidden="true" /> {vm.paymentStatus}
        </p>
      )}
      {/* Поздний слип — одно место: окно оплаты; пока оно закрыто — строка, которая его открывает. */}
      {vm.latePayment != null && (
        <p className="rmk-status__line" role="alert" data-tone="danger">
          <CircleAlert size={14} aria-hidden="true" /> {vm.latePayment.text}{' '}
          <Button variant="primary" icon={<CreditCard size={14} aria-hidden="true" />} onClick={onShowPayment}>
            Решить в окне оплаты
          </Button>
        </p>
      )}
      {till.backgroundWork == null && vm.parkedNotice != null && (
        <p className="rmk-status__line" role="status">
          <PauseCircle size={14} aria-hidden="true" /> {vm.parkedNotice.text}{' '}
          {onOpenParked !== undefined && (
            <Button variant="ghost" onClick={onOpenParked}>
              {vm.parkedNotice.linkLabel}
            </Button>
          )}
        </p>
      )}
      {(vm.notices ?? []).map((item) =>
        item.tone === 'danger' || item.tone === 'warning' ? (
          <RmkNotice
            key={item.key}
            tone={item.tone}
            title={item.tone === 'danger' ? 'Ошибка' : 'Внимание'}
            text={item.text}
            actions={
              item.actionLabel !== null && onNoticeAction !== undefined ? (
                <Button variant="ghost" busy={item.busy} onClick={() => onNoticeAction(item.key)}>
                  {item.actionLabel}
                </Button>
              ) : undefined
            }
            onClose={onNoticeDismiss === undefined || item.busy ? undefined : () => onNoticeDismiss(item.key)}
          />
        ) : (
          <p key={item.key} className="rmk-status__line" role="status" data-tone={item.tone}>
            <CircleAlert size={14} aria-hidden="true" /> {item.text}{' '}
            {item.actionLabel !== null && onNoticeAction !== undefined && (
              <Button variant="ghost" busy={item.busy} onClick={() => onNoticeAction(item.key)}>
                {item.actionLabel}
              </Button>
            )}
            {onNoticeDismiss !== undefined && (
              <Button variant="ghost" disabled={item.busy} onClick={() => onNoticeDismiss(item.key)}>
                Понятно
              </Button>
            )}
          </p>
        ),
      )}
      {vm.payment?.collapsed === true && (
        <Button variant="primary" icon={<Activity size={15} aria-hidden="true" />} onClick={onShowPayment}>
          Ход оплаты: {vm.payment.title}
        </Button>
      )}
      {till.backgroundWork}
    </section>
  )
}

/** Телефон: итог и «Наличными / Картой» — у нижнего края, всегда под пальцем. */
export function CheckoutDock({ vm, till, onPay }: { vm: SaleVm; till: CheckoutTill; onPay: (kind: PaymentKind) => void }) {
  return (
    <div className="rmk-dock" role="group" aria-label="Итог и оплата">
      <Total vm={vm} />
      <Payments vm={vm} till={till} onPay={onPay} />
    </div>
  )
}

function Total({ vm }: { vm: SaleVm }) {
  return (
    <div className="rmk-checkout__total">
      <span>Итого</span>
      <strong data-testid="rmk-total">{vm.totalText}</strong>
      {vm.totalPending && <small>итог обновит касса после сохранения</small>}
    </div>
  )
}

/** Кнопки оплаты — всегда на месте у нижнего края; почему они выключены, сказано в блоке состояния и в подсказке кнопки. */
function Payments({ vm, till, onPay }: { vm: SaleVm; till: CheckoutTill; onPay: (kind: PaymentKind) => void }) {
  const reason = payBlockedText(vm, till)
  const blocked = till.payBlockedReason !== null
  return (
    <div className="rmk-checkout__pay">
      <div className="rmk-checkout__payments" role="group" aria-label="Способ оплаты">
        {vm.payButtons.map((button) => (
          <Button
            key={button.kind}
            variant={button.primary ? 'primary' : 'secondary'}
            busy={button.busy}
            disabled={!button.enabled || blocked}
            title={!button.enabled || blocked ? reason ?? undefined : undefined}
            onClick={() => onPay(button.kind)}
          >
            {button.label}
          </Button>
        ))}
      </div>
    </div>
  )
}
